import { useEffect, useRef } from 'react';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

// Custom video embed format
const BlockEmbed = Quill.import('blots/block/embed');

class VideoBlot extends BlockEmbed {
  static create(url) {
    const node = super.create();
    node.setAttribute('frameborder', '0');
    node.setAttribute('allowfullscreen', 'true');
    node.setAttribute('class', 'ql-video');
    
    // Handle YouTube and Vimeo URLs
    if (url.includes('youtube.com') || url.includes('youtu.be')) {
      const videoId = url.includes('youtu.be') 
        ? url.split('youtu.be/')[1]?.split('?')[0]
        : url.split('v=')[1]?.split('&')[0];
      node.setAttribute('src', `https://www.youtube.com/embed/${videoId}`);
    } else if (url.includes('vimeo.com')) {
      const videoId = url.split('vimeo.com/')[1]?.split('?')[0];
      node.setAttribute('src', `https://player.vimeo.com/video/${videoId}`);
    } else {
      node.setAttribute('src', url);
    }
    
    return node;
  }

  static value(node) {
    return node.getAttribute('src');
  }
}

VideoBlot.blotName = 'video';
VideoBlot.tagName = 'iframe';

try {
  Quill.register(VideoBlot);
} catch (e) {
  // Already registered
}

export function QuillEditor({ value, onChange, onImageUpload }) {
  const editorRef = useRef(null);
  const quillInstance = useRef(null);
  const isUpdating = useRef(false);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!editorRef.current || quillInstance.current || isInitialized.current) return;
    
    isInitialized.current = true;

    // Initialize Quill
    const quill = new Quill(editorRef.current, {
      theme: 'snow',
      modules: {
        toolbar: {
          container: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['blockquote'],
            [{ 'align': [] }],
            ['link', 'image', 'video'],
            ['clean']
          ],
          handlers: {
            image: function() {
              const input = document.createElement('input');
              input.setAttribute('type', 'file');
              input.setAttribute('accept', 'image/*');
              input.click();

              input.onchange = async () => {
                const file = input.files[0];
                if (file && onImageUpload) {
                  try {
                    const url = await onImageUpload(file);
                    if (quill) {
                      const range = quill.getSelection(true);
                      quill.insertEmbed(range.index, 'image', url);
                      quill.setSelection(range.index + 1);
                    }
                  } catch (err) {
                    console.error('Image upload failed:', err);
                  }
                }
              };
            },
            video: function() {
              const url = prompt('Enter YouTube or Vimeo URL:');
              if (url && quill) {
                const range = quill.getSelection(true);
                quill.insertEmbed(range.index, 'video', url);
                quill.setSelection(range.index + 1);
              }
            }
          }
        }
      },
      formats: [
        'header',
        'bold', 'italic', 'underline',
        'list', 'bullet',
        'blockquote',
        'align',
        'link', 'image', 'video'
      ]
    });

    // Set initial content
    if (value) {
      console.log('QuillEditor: Setting initial content, length:', value.length);
      quill.root.innerHTML = value;
    } else {
      console.log('QuillEditor: No initial content provided');
    }

    // Handle text changes
    quill.on('text-change', () => {
      if (!isUpdating.current && onChange) {
        const html = quill.root.innerHTML;
        onChange(html);
      }
    });

    quillInstance.current = quill;

    return () => {
      if (quillInstance.current) {
        quillInstance.current = null;
      }
    };
  }, [onImageUpload, onChange]);

  // Update content when value prop changes
  useEffect(() => {
    if (quillInstance.current && value !== undefined && value !== null) {
      const currentContent = quillInstance.current.root.innerHTML;
      // Normalize comparison - Quill adds <p><br></p> for empty content
      const normalizedCurrent = currentContent === '<p><br></p>' ? '' : currentContent;
      const normalizedValue = value === '<p><br></p>' ? '' : value;
      
      console.log('QuillEditor: Content update triggered', {
        valueLength: value.length,
        currentLength: currentContent.length,
        needsUpdate: normalizedCurrent !== normalizedValue
      });
      
      if (normalizedCurrent !== normalizedValue) {
        isUpdating.current = true;
        quillInstance.current.root.innerHTML = value;
        isUpdating.current = false;
        console.log('QuillEditor: Content updated');
      }
    }
  }, [value]);

  return (
    <div className="quill-editor-wrapper">
      <div ref={editorRef} style={{ minHeight: '400px' }} />
      <style jsx global>{`
        .quill-editor-wrapper {
          background: hsl(var(--bk-bg));
          border-radius: var(--radius);
        }
        
        .quill-editor-wrapper .ql-toolbar {
          background: hsl(var(--bk-bg-elev));
          border: 1px solid hsl(var(--bk-line));
          border-radius: var(--radius) var(--radius) 0 0;
        }
        
        .quill-editor-wrapper .ql-container {
          background: hsl(var(--bk-bg));
          border: 1px solid hsl(var(--bk-line));
          border-top: none;
          border-radius: 0 0 var(--radius) var(--radius);
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 16px;
          color: hsl(var(--bk-fg));
          min-height: 400px;
        }
        
        .quill-editor-wrapper .ql-editor {
          min-height: 400px;
          padding: 20px;
        }
        
        .quill-editor-wrapper .ql-editor.ql-blank::before {
          color: hsl(var(--bk-fg-subtle));
          font-style: italic;
        }
        
        .quill-editor-wrapper .ql-stroke {
          stroke: hsl(var(--bk-fg-muted));
        }
        
        .quill-editor-wrapper .ql-fill {
          fill: hsl(var(--bk-fg-muted));
        }
        
        .quill-editor-wrapper .ql-picker-label {
          color: hsl(var(--bk-fg-muted));
        }
        
        .quill-editor-wrapper .ql-toolbar button:hover,
        .quill-editor-wrapper .ql-toolbar button:focus,
        .quill-editor-wrapper .ql-toolbar button.ql-active {
          color: hsl(var(--bk-accent));
        }
        
        .quill-editor-wrapper .ql-toolbar button:hover .ql-stroke,
        .quill-editor-wrapper .ql-toolbar button:focus .ql-stroke,
        .quill-editor-wrapper .ql-toolbar button.ql-active .ql-stroke {
          stroke: hsl(var(--bk-accent));
        }
        
        .quill-editor-wrapper .ql-toolbar button:hover .ql-fill,
        .quill-editor-wrapper .ql-toolbar button:focus .ql-fill,
        .quill-editor-wrapper .ql-toolbar button.ql-active .ql-fill {
          fill: hsl(var(--bk-accent));
        }
        
        .quill-editor-wrapper .ql-video {
          width: 100%;
          aspect-ratio: 16/9;
          margin: 20px 0;
          border-radius: var(--radius);
        }
        
        .quill-editor-wrapper .ql-editor img {
          max-width: 100%;
          border-radius: var(--radius);
          margin: 20px 0;
        }
        
        .quill-editor-wrapper .ql-editor blockquote {
          border-left: 4px solid hsl(var(--bk-accent));
          padding-left: 16px;
          margin: 20px 0;
          font-style: italic;
        }
        
        .quill-editor-wrapper .ql-editor a {
          color: hsl(var(--bk-accent));
          text-decoration: none;
        }
        
        .quill-editor-wrapper .ql-editor a:hover {
          opacity: 0.8;
        }
      `}</style>
    </div>
  );
}
